import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Filter, RefreshCw } from 'lucide-react';
import { fetchCampaignsStats } from '../redux/slices/campaignSlice';
import CampaignTypeBadge from '../components/CampaignTypeBadge';
import {
  mapDashboardDateRangeToStats,
  resolveCampaignPersonalized,
} from '../utils/campaignTypeDisplay';

const AllCampaigns = ({ defaultDateRange = 'today' }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const initialDateRange = mapDashboardDateRangeToStats(
    location.state?.dateRange || defaultDateRange
  );

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [sortBy, setSortBy] = useState('ctr');
  const [order, setOrder] = useState('desc');
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [typeFilter, setTypeFilter] = useState('all');

  const {
    campaignStats,
    campaignStatsPagination,
    campaignStatsLoading,
    campaignStatsError,
  } = useSelector((state) => state.campaigns);

  const fetchPage = typeFilter === 'all' ? page : 1;
  const fetchLimit = typeFilter === 'all' ? limit : 500;

  useEffect(() => {
    dispatch(fetchCampaignsStats({
      page: fetchPage,
      limit: fetchLimit,
      sortBy,
      order,
      dateRange,
    }));
  }, [dispatch, fetchPage, fetchLimit, sortBy, order, dateRange]);

  useEffect(() => {
    setPage(1);
  }, [dateRange, sortBy, order, typeFilter]);

  const filteredCampaignStats = useMemo(() => {
    if (typeFilter === 'all') return campaignStats;

    return campaignStats.filter((campaign) => {
      const personalized = resolveCampaignPersonalized(campaign);
      return typeFilter === 'personalized' ? personalized : !personalized;
    });
  }, [campaignStats, typeFilter]);

  const visibleCampaignStats = useMemo(() => {
    if (typeFilter === 'all') return filteredCampaignStats;

    const start = (page - 1) * limit;
    return filteredCampaignStats.slice(start, start + limit);
  }, [filteredCampaignStats, typeFilter, page, limit]);

  const paginationInfo = useMemo(() => {
    if (typeFilter === 'all') {
      return {
        page: campaignStatsPagination?.page || page,
        total: campaignStatsPagination?.total ?? campaignStats.length,
        totalPages: campaignStatsPagination?.totalPages || 1,
        hasPrev: campaignStatsPagination?.hasPrev ?? page > 1,
        hasNext: campaignStatsPagination?.hasNext ?? false,
      };
    }

    const total = filteredCampaignStats.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));

    return {
      page,
      total,
      totalPages,
      hasPrev: page > 1,
      hasNext: page < totalPages,
    };
  }, [typeFilter, campaignStatsPagination, page, limit, filteredCampaignStats.length, campaignStats.length]);

  const dateRangeOptions = [
    { value: 'today', label: 'Bugün' },
    { value: 'week', label: 'Son 7 gün' },
    { value: 'month', label: 'Son 30 gün' },
    { value: 'year', label: 'Son 365 gün' },
    { value: 'all', label: 'Tüm Zamanlar' },
  ];

  const dateRangeLabel = dateRangeOptions.find((opt) => opt.value === dateRange)?.label || dateRange;

  const handleTypeFilter = (type) => {
    setPage(1);
    setTypeFilter(type);
  };

  const emptyMessage =
    typeFilter === 'general'
      ? 'Genel kampanya bulunamadı'
      : typeFilter === 'personalized'
        ? 'Bana özel kampanya bulunamadı'
        : 'Henüz kampanya verisi yok';

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tüm Kampanyalar</h1>
          <p className="text-sm text-gray-500 mt-1">
            {dateRangeLabel} · kampanya bazlı görüntülenme, tıklanma ve CTR
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <CampaignTypeBadge
              variant="total"
              compact={false}
              active={typeFilter === 'all'}
              onClick={() => handleTypeFilter('all')}
            />
            <CampaignTypeBadge
              personalized={false}
              compact={false}
              active={typeFilter === 'general'}
              onClick={() => handleTypeFilter('general')}
            />
            <CampaignTypeBadge
              personalized={true}
              compact={false}
              active={typeFilter === 'personalized'}
              onClick={() => handleTypeFilter('personalized')}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="pl-9 pr-3 py-2 bg-gray-50 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              {dateRangeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 bg-gray-50 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="ctr">CTR</option>
              <option value="impressions">Görüntülenme</option>
              <option value="clicks">Tıklanma</option>
              <option value="campaignTitle">Kampanya Adı</option>
            </select>
            <select
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              className="px-3 py-2 bg-gray-50 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="desc">Azalan</option>
              <option value="asc">Artan</option>
            </select>
          </div>
          <button
            onClick={() => dispatch(fetchCampaignsStats({
              page: typeFilter === 'all' ? page : 1,
              limit: typeFilter === 'all' ? limit : 500,
              sortBy,
              order,
              dateRange,
            }))}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
          >
            <RefreshCw size={16} /> Yenile
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Kampanya</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Görüntülenme</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tıklanma</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">CTR (%)</th>
              </tr>
            </thead>
            <tbody>
              {campaignStatsLoading ? (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-gray-400">Yükleniyor...</td>
                </tr>
              ) : campaignStatsError ? (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-red-500 text-sm">{campaignStatsError}</td>
                </tr>
              ) : visibleCampaignStats.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-gray-400">{emptyMessage}</td>
                </tr>
              ) : (
                visibleCampaignStats.map((c) => {
                  const personalized = resolveCampaignPersonalized(c);
                  return (
                    <tr
                      key={c.campaignId}
                      className={`border-b border-gray-100 transition-colors border-l-4 ${
                        personalized
                          ? 'border-l-violet-500 bg-violet-50/60 hover:bg-violet-50'
                          : 'border-l-transparent hover:bg-gray-50'
                      }`}
                    >
                      <td className="py-2 px-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                          <CampaignTypeBadge campaign={c} />
                          <span className="text-sm font-medium text-gray-900">{c.campaignTitle || c.campaignId}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-sm text-right text-gray-700">
                        {(c.impressions ?? 0).toLocaleString('tr-TR')}
                      </td>
                      <td className="py-2 px-3 text-sm text-right text-gray-700">{(c.clicks ?? 0).toLocaleString('tr-TR')}</td>
                      <td className={`py-2 px-3 text-sm text-right font-semibold ${personalized ? 'text-violet-700' : 'text-green-600'}`}>
                        {(c.ctr ?? 0).toFixed(2)}%
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-100">
          <div className="text-sm text-gray-500">
            Sayfa {paginationInfo.page} / {paginationInfo.totalPages} — Toplam {paginationInfo.total} kampanya
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={!paginationInfo.hasPrev}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft size={16} /> Önceki
            </button>
            <button
              disabled={!paginationInfo.hasNext}
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Sonraki <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllCampaigns;
